import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PosterPackButton } from "../components/PosterPackButton";
import { CommsPanel } from "../components/samourais/CommsPanel";
import { CarnetDrawer } from "../components/samourais/CarnetDrawer";
import { EquipmentOrdersPanel } from "../components/samourais/EquipmentOrdersPanel";
import { CurriculumEditor } from "../components/samourais/CurriculumEditor";
import { RemisesPanel } from "../components/samourais/RemisesPanel";
import {
  Baby, Plus, RefreshCw, Link2, Trash2, Send, Award, Users, DollarSign,
  Clock, CheckCircle2, ChevronDown, ChevronRight, Image as ImageIcon, Download,
  ClipboardList, Bell, NotebookPen, CalendarCheck, FileText, AlertTriangle, X,
} from "lucide-react";

const FOLLOWUP_STATUS = {
  nouveau: { label: "Nouveau", cls: "bg-sky-500/20 text-sky-300" },
  a_relancer: { label: "À relancer", cls: "bg-amber-500/20 text-amber-300" },
  relance: { label: "Relancé", cls: "bg-violet-500/20 text-violet-300" },
  confirme: { label: "Confirmé", cls: "bg-emerald-500/20 text-emerald-300" },
  absent_repete: { label: "Absent répété", cls: "bg-red-500/20 text-red-300" },
};

const PUBLIC_URL = `${process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "")}/petits-samourais`;

const KPI = ({ label, value, accent = "text-zinc-100" }) => (
  <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4">
    <div className="text-xs text-zinc-400">{label}</div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
  </div>
);

export default function PetitsSamouraisModule({ embedded = false } = {}) {
  const [dash, setDash] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [newS, setNewS] = useState({ title: "Petits Samouraïs — Bloc", days: "Lundi & mercredi", time: "17h30 à 18h00", weeks: 8, start_date: "2026-09-09", capacity: 12, status: "preinscription", notes: "" });
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterFmt, setPosterFmt] = useState(null);
  const [posterProgress, setPosterProgress] = useState(0);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [bcMsg, setBcMsg] = useState("🥋 Rappel important — la nouvelle horaire commence AUJOURD'HUI ! Le cours des Petits Samouraïs est à 18h. On a hâte de vous voir sur le tatami ! — Institut Impact Élite Martial");
  const [bcSms, setBcSms] = useState(true);
  const [bcEmail, setBcEmail] = useState(true);
  const [bcSending, setBcSending] = useState(false);
  const [bcHistory, setBcHistory] = useState([]);
  const [genWaitlist, setGenWaitlist] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const [followupOpen, setFollowupOpen] = useState({});
  const [appelSession, setAppelSession] = useState(null);
  const [bilanDuo, setBilanDuo] = useState(null);
  const [carnetDuo, setCarnetDuo] = useState(null);
  const [absenceAlerts, setAbsenceAlerts] = useState([]);

  const loadAlerts = useCallback(async () => {
    try { const { data } = await api.get("/samourais/absence-alerts"); setAbsenceAlerts(data.alerts || []); }
    catch { /* non bloquant */ }
  }, []);

  // 🔎 Recherche prospects Petits Samouraïs (toutes sources + Gmail)
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchIncludeGmail, setSearchIncludeGmail] = useState(true);
  const [searchRes, setSearchRes] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [inviteChannel, setInviteChannel] = useState("email");

  const runProspectSearch = async () => {
    setSearchBusy(true);
    try {
      const r = await api.get(`/samourais/prospects-search`, { params: { include_gmail: searchIncludeGmail, date_from: dateFrom, date_to: dateTo } });
      setSearchRes(r.data);
      toast.success(`${r.data.total} prospect(s) trouvé(s)${r.data.new_count ? ` · ${r.data.new_count} nouveau(x)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de recherche"); }
    finally { setSearchBusy(false); }
  };

  const toggleContacted = async (p) => {
    const next = !p.contacted;
    // maj optimiste
    setSearchRes((prev) => prev ? {
      ...prev,
      prospects: prev.prospects.map((x) => x.key === p.key ? { ...x, contacted: next } : x),
      contacted_count: prev.contacted_count + (next ? 1 : -1),
    } : prev);
    try {
      await api.post(`/samourais/prospects/mark-contacted`, { key: p.key, contacted: next });
    } catch { toast.error("Erreur — impossible de marquer"); runProspectSearch(); }
  };

  const exportProspectsCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
    const token = localStorage.getItem("map_token") || "";
    const qs = new URLSearchParams({ include_gmail: searchIncludeGmail, date_from: dateFrom, date_to: dateTo }).toString();
    fetch(`${base}/api/samourais/prospects-search/export.csv?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "prospects-petits-samourais.csv";
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Échec de l'export CSV"));
  };

  const inviteToRentree = async () => {
    const withEmail = searchRes?.with_email || 0;
    const withPhone = (searchRes?.prospects || []).filter((p) => p.phone && !p.contacted).length;
    const chanLabel = inviteChannel === "sms" ? "par SMS" : inviteChannel === "both" ? "par courriel + SMS" : "par courriel";
    if (!withEmail && !withPhone) { toast.error("Aucun prospect à inviter."); return; }
    if (!window.confirm(`Envoyer l'invitation à la rentrée du mercredi 9 septembre 2026 ${chanLabel} ?\n\n(Les familles déjà contactées sont automatiquement ignorées.)`)) return;
    setInviting(true);
    try {
      const r = await api.post(`/samourais/prospects-invite`, { include_gmail: searchIncludeGmail, channel: inviteChannel, date_from: dateFrom, date_to: dateTo });
      toast.success(`Invitation envoyée : ${r.data.email_sent || 0} courriel(s) · ${r.data.sms_sent || 0} SMS`);
      runProspectSearch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
    finally { setInviting(false); }
  };

  const notifyWaitlist = async () => {
    const openS = sessions.find((s) => ["preinscription", "decouverte", "actif"].includes(s.status));
    if (!openS) { toast.error("Aucune session ouverte — ouvre d'abord un groupe."); return; }
    if (!window.confirm(`Envoyer un courriel de relance à ${genWaitlist.length} famille(s) de la liste d'attente pour « ${openS.title} » ?\n\n(Les familles déjà relancées pour cette session ne recevront pas de doublon.)`)) return;
    setNotifying(true);
    try {
      const r = await api.post(`/samourais/sessions/${openS.id}/notify-waitlist`);
      toast.success(`Relance envoyée : ${r.data.sent} courriel(s)${r.data.failed ? `, ${r.data.failed} échec(s)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setNotifying(false); }
  };

  const loadWaitlist = useCallback(async () => {
    try { const r = await api.get("/samourais/waitlist-general"); setGenWaitlist(r.data.waitlist || []); } catch { /* non bloquant */ }
  }, []);
  const removeWaitlist = async (id) => {
    try { await api.delete(`/samourais/waitlist-general/${id}`); toast.success("Retiré"); loadWaitlist(); } catch { toast.error("Erreur"); }
  };

  const loadHistory = useCallback(async () => {
    try { const r = await api.get("/samourais/broadcasts"); setBcHistory(r.data.broadcasts || []); } catch { /* non bloquant */ }
  }, []);

  const sendBroadcast = async () => {
    if (!bcMsg.trim()) { toast.error("Message vide."); return; }
    if (!window.confirm("Envoyer ce message à TOUS les parents inscrits (SMS + courriel) ?")) return;
    setBcSending(true);
    try {
      const r = await api.post("/samourais/broadcast", { message: bcMsg, send_sms: bcSms, send_email: bcEmail });
      toast.success(`Envoyé à ${r.data.recipient_count} parent(s) — ${r.data.sms_sent} SMS, ${r.data.email_sent} courriels.`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} échec(s) — voir détails.`);
      setShowBroadcast(false);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi groupé."); }
    setBcSending(false);
  };

  const genPoster = async (fmt) => {
    setPosterFmt(fmt); setPosterUrl(null); setPosterProgress(0);
    const start = Date.now();
    const timer = setInterval(() => {
      setPosterProgress(Math.min(95, Math.round(((Date.now() - start) / 70000) * 100)));
    }, 500);
    try {
      const r = await api.post("/poster/samourais", { format: fmt });
      // Génération asynchrone (arrière-plan) : sonde la tâche jusqu'à achèvement.
      let result = r.data.result;
      if (r.data.job_id) {
        const deadline = Date.now() + 240000;
        while (Date.now() < deadline) {
          await new Promise((res) => setTimeout(res, 3000));
          const s = await api.get(`/poster/job/${r.data.job_id}`);
          if (s.data.status === "done") { result = s.data.result || {}; break; }
          if (s.data.status === "error") throw new Error(s.data.error || "Échec de la génération");
        }
        if (!result) throw new Error("Délai dépassé — réessaie.");
      }
      setPosterProgress(100);
      const rel = result.url || result.full_url;
      const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
      setPosterUrl(rel?.startsWith("http") ? rel : `${base}${rel}`);
      toast.success("Affiche générée !");
    } catch (e) {
      toast.error(e?.message || e.response?.data?.detail || "Échec de génération");
    } finally { clearInterval(timer); setPosterFmt(null); }
  };

  const downloadPoster = async () => {
    if (!posterUrl) return;
    try {
      const res = await fetch(posterUrl);
      if (!res.ok) throw new Error("fichier indisponible");
      const blob = await res.blob();
      if (!blob.type.startsWith("image/")) throw new Error("format invalide");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "affiche-petits-samourais.png";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Téléchargement impossible — régénère l'affiche.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([api.get("/samourais/dashboard"), api.get("/samourais/sessions")]);
      setDash(d.data.kpis); setSessions(s.data.sessions || []);
    } catch { toast.error("Erreur de chargement"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadWaitlist(); }, [loadWaitlist]);

  const seed = async () => { try { await api.post("/samourais/seed"); toast.success("Session de lancement créée"); load(); } catch { toast.error("Erreur"); } };

  const createSession = async () => {
    try { await api.post("/samourais/sessions", newS); toast.success("Session créée"); setShowNew(false); load(); }
    catch { toast.error("Erreur de création"); }
  };

  const delSession = async (id) => {
    if (!window.confirm("Supprimer cette session et ses inscriptions ?")) return;
    try { await api.delete(`/samourais/sessions/${id}`); toast.success("Supprimée"); load(); } catch { toast.error("Erreur"); }
  };

  const resyncStripe = async (sid, eid) => {
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${eid}/resync-stripe`);
      if (r.data.result === "stripe_confirme") toast.success(`✅ ${r.data.child_name} : paiement confirmé dans Stripe (${r.data.amount} $) — dossier mis à jour`);
      else toast.info(`${r.data.child_name} : aucun paiement Stripe trouvé (${r.data.result})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur resync"); }
  };
  const manualPayment = async (sid, e) => {
    const amount = window.prompt(`Paiement manuel — ${e.child_name}\nMontant reçu ($) :`, e.amount ?? "");
    if (!amount) return;
    const method = window.prompt("Méthode : comptant / interac / virement / cheque / autre", "interac");
    if (!method) return;
    const reference = window.prompt("Référence (n° Interac, chèque…) — optionnel", "") || "";
    const verified = window.confirm("Le paiement est-il VÉRIFIÉ (argent reçu) ?\nOK = confirmé · Annuler = « à vérifier »");
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${e.eid}/manual-payment`, { amount: parseFloat(amount), method, reference, verified });
      toast.success(`Paiement manuel enregistré → ${r.data.payment_state}`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const sendLink = async (sid, eid) => {
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${eid}/payment-link`);
      if (r.data.ok) toast.success(`Lien envoyé${r.data.email_sent ? " (courriel)" : ""}${r.data.sms_sent ? " + SMS" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
  };

  const parentLink = async (sid, e) => {
    const send = window.confirm(`Espace parent — ${e.child_name}\nOK = générer ET envoyer le lien au parent (courriel + SMS)\nAnnuler = seulement copier le lien`) ? "both" : "none";
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${e.eid}/parent-link`, { send, base_url: window.location.origin });
      try { await navigator.clipboard.writeText(r.data.url); } catch { /* clipboard indisponible */ }
      toast.success(send === "both" ? `Lien envoyé (${r.data.sent.email ? "courriel" : ""}${r.data.sent.email && r.data.sent.sms ? " + " : ""}${r.data.sent.sms ? "SMS" : ""}) et copié` : "Lien espace parent copié");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const addCert = async (sid, eid) => {
    const type = window.prompt("Type de certificat (participation, evolution, courage, respect, confiance…)", "participation");
    if (!type) return;
    try { await api.post(`/samourais/sessions/${sid}/certificate`, { eid, type }); toast.success("Certificat ajouté"); load(); }
    catch { toast.error("Erreur"); }
  };

  const delDuo = async (sid, eid) => {
    if (!window.confirm("Retirer ce duo ?")) return;
    try { await api.delete(`/samourais/sessions/${sid}/enrollment/${eid}`); toast.success("Retiré"); load(); } catch { toast.error("Erreur"); }
  };

  const copyLink = () => { navigator.clipboard.writeText(PUBLIC_URL); toast.success("Lien public copié"); };

  const [contractsStatus, setContractsStatus] = useState(null);
  const loadContractsStatus = useCallback(async () => {
    try { const r = await api.get("/samourais/contracts/status"); setContractsStatus(r.data); } catch { /* silencieux */ }
  }, []);
  useEffect(() => { loadContractsStatus(); }, [loadContractsStatus, sessions]);
  const backfillContracts = async () => {
    if (!window.confirm("Créer un contrat élève standard pour chaque famille Petits Samouraïs sans contrat ?\n(Idempotent — les contrats existants sont seulement mis à jour.)")) return;
    try {
      const r = await api.post("/samourais/contracts/backfill");
      toast.success(`Contrats : ${r.data.created} créé(s) · ${r.data.updated} mis à jour`);
      load(); loadContractsStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="petits-samourais-module" className="text-zinc-100 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center">
            <Baby className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={embedded ? "text-xl font-bold" : "text-3xl font-bold"}>Petits Samouraïs{embedded ? <span className="text-zinc-500 text-sm font-normal"> · programme intégré aux Contrats Élèves</span> : null}</h1>
            <p className="text-zinc-400 text-sm">Parents-Enfants 3-6 ans · lundi & mercredi 17h30-18h</p>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
              <a href="/petits-samourais" target="_blank" rel="noreferrer" className="text-sky-300 hover:underline" data-testid="ps-link-public">🌐 Page publique</a>
              <a href="/app/presences" className="text-sky-300 hover:underline">🖥️ Présences</a>
              <a href="/app/rencontre-rentree" className="text-sky-300 hover:underline">🎒 Rentrée</a>
              <a href="/app/evenements" className="text-sky-300 hover:underline">📅 Événements</a>
              <a href="/app/taches" className="text-sky-300 hover:underline">✅ Tâches</a>
              <a href="/app/eleves" className="text-sky-300 hover:underline">📄 Contrats élèves</a>
            </div>
          </div>
        </div>
        {contractsStatus && (
          <div className={`w-full rounded-lg border px-3 py-2 text-xs flex flex-wrap items-center gap-2 ${contractsStatus.missing > 0 ? "border-red-500/40 bg-red-500/10 text-red-100" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"}`} data-testid="ps-contracts-status">
            📄 <b>Contrats élèves :</b> {contractsStatus.linked}/{contractsStatus.total} famille(s) reliée(s)
            {contractsStatus.missing > 0 && <span>· <b>{contractsStatus.missing} sans contrat</b> — elles n'apparaissent pas dans Rentrée / Tâches / Présences.</span>}
            {contractsStatus.missing > 0 && (
              <button onClick={backfillContracts} data-testid="ps-backfill-contracts-btn" className="ml-auto px-2.5 py-1 rounded-md bg-red-600 hover:bg-red-500 text-white font-semibold">Créer les contrats manquants</button>
            )}
          </div>
        )}
        <div className="w-full rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="ps-stripe-warning">
          ⚠️ <b>Avant toute relance « impayé » : vérifier Stripe.</b> Un dossier non payé s'affiche « rapprochement requis » (jamais « impayé » automatiquement). Boutons par famille : <b>↻$</b> resynchroniser Stripe · <b>💵</b> paiement manuel/virement. La relance est bloquée automatiquement si Stripe confirme le paiement. Extras uniforme retirés du formulaire public (prix à valider).
        </div>
        <div className="flex gap-2">
          <button data-testid="samourais-broadcast-btn" onClick={() => setShowBroadcast(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm">
            <Send className="w-4 h-4" /> Message à tous
          </button>
          <button data-testid="samourais-copy-link" onClick={copyLink}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
            <Link2 className="w-4 h-4" /> Lien public
          </button>
          <button data-testid="samourais-refresh" onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
      </div>

      <CommsPanel />
      <RemisesPanel />
      <EquipmentOrdersPanel />
      <CurriculumEditor />
      {carnetDuo && <CarnetDrawer sid={carnetDuo.sid} enr={carnetDuo.enr} onClose={() => setCarnetDuo(null)} reload={load} />}

      {/* ⚠️ Alerte : 2 absences de suite */}
      {absenceAlerts.length > 0 && (
        <div className="mb-6 rounded-2xl border border-red-500/50 bg-red-500/10 px-4 py-3" data-testid="samourais-absence-alert">
          <div className="flex items-center gap-2 text-red-200 font-semibold text-sm mb-1.5">
            <AlertTriangle className="w-4 h-4" /> {absenceAlerts.length} enfant(s) avec 2 absences de suite — à relancer
          </div>
          <div className="flex flex-wrap gap-2">
            {absenceAlerts.map((a) => (
              <span key={a.eid} className="text-xs bg-red-950/50 border border-red-500/30 rounded-full pl-2.5 pr-1 py-1 text-red-100 inline-flex items-center gap-1.5" data-testid={`absence-alert-${a.eid}`}>
                {a.child_name} <span className="text-red-300/70">· {a.session_title}</span>
                <button onClick={async () => {
                  const first = (a.parent_name || "").split(" ")[0] || "";
                  const message = `Bonjour ${first} ! On s'ennuie de ${a.child_name} au dojo 🥋 On aimerait beaucoup le/la revoir au prochain cours des Petits Samouraïs. Est-ce qu'on peut vous aider avec l'horaire ? Au plaisir !`;
                  try { await api.post(`/samourais/sessions/${a.session_id}/enrollment/${a.eid}/reminder`, { message, channel: "both" }); toast.success(`Relance envoyée à la famille de ${a.child_name}`); loadAlerts(); load(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
                }} data-testid={`absence-relance-${a.eid}`}
                  className="ml-1 text-[10px] bg-red-600 hover:bg-red-500 text-white font-semibold rounded-full px-2 py-0.5">💬 Relancer</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bandeau rentrée */}
      <div className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3 flex items-center gap-3" data-testid="samourais-rentree-banner">
        <span className="text-2xl">🗓️</span>
        <div className="text-sm">
          <span className="text-red-200 font-semibold">Rentrée : Mercredi 9 septembre 2026</span>
          <span className="text-zinc-400"> — Petits Samouraïs Parents-Enfants (3-6 ans) · Lundi & mercredi 17h30-18h</span>
        </div>
      </div>

      {/* 🔎 Recherche prospects Petits Samouraïs (toutes sources + Gmail) */}
      <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="samourais-prospect-search">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Recherche prospects — cours parent-enfant</h3>
            <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
              Regroupe TOUT le monde ayant demandé des infos sur les Petits Samouraïs (3-6 ans) :
              liste d'attente, préinscriptions/essais, leads des pages &amp; QR, et courriels Gmail.
            </p>
            <label className="flex items-center gap-2 text-xs text-zinc-300 mt-2 cursor-pointer">
              <input type="checkbox" checked={searchIncludeGmail} onChange={(e) => setSearchIncludeGmail(e.target.checked)} data-testid="search-include-gmail" />
              Inclure la recherche dans Gmail (nécessite Gmail connecté)
            </label>
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Du</div>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="prospect-date-from"
                  className="bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-200" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Au</div>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="prospect-date-to"
                  className="bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-200" />
              </div>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-zinc-400 underline pb-1.5" data-testid="clear-date-filter">Effacer</button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={runProspectSearch} disabled={searchBusy} data-testid="run-prospect-search"
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50">
              {searchBusy ? "⏳ Recherche…" : "🔎 Lancer la recherche"}
            </button>
          </div>
        </div>

        {searchRes && (
          <div className="mt-4 space-y-3" data-testid="prospect-search-results">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="px-2 py-1 rounded bg-zinc-800 text-zinc-200">Total : <b>{searchRes.total}</b></span>
              <span className="px-2 py-1 rounded bg-zinc-800 text-zinc-200">Avec courriel : <b>{searchRes.with_email}</b></span>
              {searchRes.new_count > 0 && <span className="px-2 py-1 rounded bg-sky-500/15 text-sky-300">🆕 Nouveaux : <b>{searchRes.new_count}</b></span>}
              <span className="px-2 py-1 rounded bg-zinc-800 text-zinc-400">✓ Contactés : <b>{searchRes.contacted_count}</b></span>
              {Object.entries(searchRes.by_origin || {}).map(([k, v]) => (
                <span key={k} className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-300">{k} : {v}</span>
              ))}
              <span className={`px-2 py-1 rounded ${searchRes.gmail_status?.startsWith("ok") ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>
                Gmail : {searchRes.gmail_status === "gmail_déconnecté" ? "déconnecté" : searchRes.gmail_status}
              </span>
              {searchRes.gmail_status === "gmail_déconnecté" && (
                <a href="/app/integrations" data-testid="reconnect-gmail-link"
                  className="px-2 py-1 rounded bg-red-500/15 text-red-300 underline hover:text-red-200">🔗 Reconnecter Gmail</a>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 bg-zinc-900 border border-white/10 rounded-lg p-0.5" data-testid="invite-channel">
                {[["email", "✉️ Courriel"], ["sms", "💬 SMS"], ["both", "✉️+💬 Les deux"]].map(([v, l]) => (
                  <button key={v} onClick={() => setInviteChannel(v)} data-testid={`channel-${v}`}
                    className={`px-2.5 py-1 rounded text-xs font-medium ${inviteChannel === v ? "bg-red-600 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
                ))}
              </div>
              <button onClick={exportProspectsCsv} data-testid="export-prospects-csv"
                className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Exporter CSV
              </button>
              <button onClick={inviteToRentree} disabled={inviting || !searchRes.total} data-testid="invite-rentree"
                className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" /> {inviting ? "Envoi…" : "Inviter à la rentrée (9 sept.)"}
              </button>
            </div>

            {searchRes.total === 0 ? (
              <p className="text-xs text-zinc-500">Aucun prospect trouvé pour le moment.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead><tr className="text-zinc-400 text-left bg-zinc-900/60">
                    <th className="py-2 px-2">Fait</th><th className="py-2 px-2">Origine</th><th className="py-2 px-2">Enfant</th><th className="py-2 px-2">Âge</th>
                    <th className="py-2 px-2">Parent</th><th className="py-2 px-2">Courriel</th><th className="py-2 px-2">Téléphone</th>
                    <th className="py-2 px-2">Source</th><th className="py-2 px-2">Date</th>
                  </tr></thead>
                  <tbody>
                    {searchRes.prospects.map((p, i) => (
                      <tr key={p.key || i} className={`border-t border-white/5 ${p.contacted ? "opacity-50" : ""}`} data-testid={`prospect-row-${i}`}>
                        <td className="py-1.5 px-2">
                          <input type="checkbox" checked={!!p.contacted} onChange={() => toggleContacted(p)}
                            data-testid={`prospect-contacted-${i}`} title="Marquer comme contacté" className="cursor-pointer" />
                        </td>
                        <td className="py-1.5 px-2 text-emerald-300 whitespace-nowrap">
                          {p.is_new && <span className="mr-1 px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 text-[9px] font-bold" data-testid={`prospect-new-${i}`}>NOUVEAU</span>}
                          {p.origin}
                        </td>
                        <td className="py-1.5 px-2 text-zinc-200">{p.child_name || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-400">{p.child_age ?? "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-200">{p.parent_name || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-300">{p.email || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-400">{p.phone || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-500">{p.source || "—"}</td>
                        <td className="py-1.5 px-2 text-zinc-500">{(p.date || "").slice(0, 10) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {genWaitlist.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="general-waitlist">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-amber-200 flex items-center gap-2">📋 Liste d'attente générale (prospects enfants) — {genWaitlist.length}</h2>
            <button onClick={notifyWaitlist} disabled={notifying} data-testid="notify-waitlist-btn"
              className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium disabled:opacity-50">
              {notifying ? "Envoi…" : "✉️ Relancer (nouvelle session)"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-3">💡 Relance automatique : dès qu'un nouveau groupe s'ouvre, ces familles reçoivent un courriel avec le lien d'inscription (chaque famille est relancée une seule fois par session).</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-400"><tr>
                <th className="text-left px-2 py-1">Enfant</th><th className="text-left px-2 py-1">Âge</th>
                <th className="text-left px-2 py-1">Parent</th><th className="text-left px-2 py-1">Courriel</th>
                <th className="text-left px-2 py-1">Tél.</th><th className="text-left px-2 py-1">Intérêt</th><th className="px-2 py-1"></th>
              </tr></thead>
              <tbody>
                {genWaitlist.map((w) => (
                  <tr key={w.id} className="border-t border-white/5" data-testid={`waitlist-row-${w.id}`}>
                    <td className="px-2 py-1.5 font-medium">{w.child_name}</td>
                    <td className="px-2 py-1.5">{w.child_age || "—"}</td>
                    <td className="px-2 py-1.5">{w.parent_name || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-400">{w.parent_email || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-400">{w.parent_phone || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-300">{ {petits_samourais:"Petits Samouraïs",enfants:"Enfants",ados:"Ados",autre:"Autre"}[w.discipline_interest] || "—"}</td>
                    <td className="px-2 py-1.5 text-right"><button onClick={() => removeWaitlist(w.id)} className="text-xs text-red-300 hover:text-red-200">Retirer</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* KPIs */}
      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <KPI label="Préinscrits" value={dash.preinscrits} />
          <KPI label="Duos confirmés" value={dash.duos_confirmes} accent="text-emerald-400" />
          <KPI label="Essais" value={dash.essais} accent="text-sky-400" />
          <KPI label="Places restantes" value={dash.spots_left} accent={dash.spots_left <= 2 ? "text-amber-300" : "text-zinc-100"} />
          <KPI label="Liste d'attente" value={dash.waitlist} accent={dash.waitlist > 0 ? "text-amber-300" : "text-zinc-100"} />
          <KPI label="Revenu payé" value={`${dash.revenue_paid} $`} accent="text-emerald-400" />
        </div>
      )}

      {/* Affiches promo IA (1-clic) */}
      <div data-testid="samourais-posters" className="rounded-xl border border-white/10 bg-zinc-900/60 p-4 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ImageIcon className="w-4 h-4 text-amber-300" />
          <h2 className="text-sm font-semibold">Affiches promo (marque Institut Impact)</h2>
        </div>
        <p className="text-xs text-zinc-400 mb-3">Génère une affiche « Petits Samouraïs » avec le QR de préinscription. Choisis le format :</p>
        <div className="flex flex-wrap gap-2">
          {[
            { k: "carre", label: "Post carré (1:1)" },
            { k: "story", label: "Story / Reel (9:16)" },
            { k: "imprimable", label: "Affiche imprimable" },
            { k: "porte", label: "Porte 120×60 cm" },
          ].map((f) => (
            <button key={f.k} data-testid={`samourais-poster-${f.k}`} onClick={() => genPoster(f.k)} disabled={!!posterFmt}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm disabled:opacity-50">
              {posterFmt === f.k ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} {f.label}
            </button>
          ))}
        </div>
        <PosterPackButton
          endpoint="/poster/samourais/pack"
          getBody={() => ({})}
          filename="pack-petits-samourais.zip"
          label="Pack 3 formats (aperçu + ZIP)"
          testId="samourais-poster-pack"
          discipline="petits_samourais"
          disabled={!!posterFmt}
          className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 text-sm font-semibold disabled:opacity-50"
        />
        {posterFmt && (
          <div className="mt-3" data-testid="samourais-poster-progress">
            <div className="text-xs text-zinc-400 mb-1">⏳ Génération en cours… ({Math.round(posterProgress)} %)</div>
            <div className="w-full max-w-md h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${posterProgress}%` }} />
            </div>
          </div>
        )}
        {posterUrl && (
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
            <img data-testid="samourais-poster-preview" src={posterUrl} alt="Affiche Petits Samouraïs"
              className="rounded-lg border border-white/10 max-h-80 object-contain bg-zinc-950" />
            <button data-testid="samourais-poster-download" onClick={downloadPoster}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-sm font-medium">
              <Download className="w-4 h-4" /> Télécharger (PNG)
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-6">
        {sessions.length === 0 && (
          <button data-testid="samourais-seed" onClick={seed}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-sm font-medium">
            <Plus className="w-4 h-4" /> Créer la session de lancement
          </button>
        )}
        <button data-testid="samourais-new-session" onClick={() => setShowNew((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
          <Plus className="w-4 h-4" /> Nouvelle session
        </button>
      </div>

      {showNew && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-4 mb-6 grid grid-cols-2 gap-3" data-testid="samourais-new-form">
          <input className="rounded-lg bg-zinc-900 border border-white/10 p-2 text-sm col-span-2" placeholder="Titre" value={newS.title} onChange={(e) => setNewS({ ...newS, title: e.target.value })} />
          <input className="rounded-lg bg-zinc-900 border border-white/10 p-2 text-sm" placeholder="Jours" value={newS.days} onChange={(e) => setNewS({ ...newS, days: e.target.value })} />
          <input className="rounded-lg bg-zinc-900 border border-white/10 p-2 text-sm" placeholder="Heure" value={newS.time} onChange={(e) => setNewS({ ...newS, time: e.target.value })} />
          <input type="date" className="rounded-lg bg-zinc-900 border border-white/10 p-2 text-sm" value={newS.start_date} onChange={(e) => setNewS({ ...newS, start_date: e.target.value })} />
          <input type="number" className="rounded-lg bg-zinc-900 border border-white/10 p-2 text-sm" placeholder="Capacité" value={newS.capacity} onChange={(e) => setNewS({ ...newS, capacity: parseInt(e.target.value) || 0 })} />
          <button onClick={createSession} data-testid="samourais-create-session" className="col-span-2 rounded-lg bg-red-600 hover:bg-red-500 py-2 text-sm font-medium">Créer</button>
        </div>
      )}

      {/* Sessions */}
      <div className="space-y-4">
        {sessions.map((s) => {
          const taken = (s.enrollments || []).length;
          const open = expanded[s.id];
          return (
            <div key={s.id} className="rounded-2xl border border-white/10 bg-zinc-900/40" data-testid={`samourais-session-row-${s.id}`}>
              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded((p) => ({ ...p, [s.id]: !p[s.id] }))}>
                <div className="flex items-center gap-2">
                  {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div>
                    <div className="font-semibold">{s.title}</div>
                    <div className="text-xs text-zinc-400">{s.days} · {s.time} · début {s.start_date} · {s.status}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm"><Users className="w-4 h-4 inline mr-1" />{taken}/{s.capacity}</span>
                  {(s.waitlist || []).length > 0 && <span className="text-xs text-amber-300">+{s.waitlist.length} attente</span>}
                  <button onClick={(e) => { e.stopPropagation(); delSession(s.id); }} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {open && (
                <div className="border-t border-white/10 p-4 space-y-2">
                  {(s.enrollments || []).length > 0 && (
                    <button onClick={() => setAppelSession(s)} data-testid={`samourais-appel-${s.id}`}
                      className="w-full mb-1 flex items-center justify-center gap-2 bg-indigo-600/80 hover:bg-indigo-600 text-white text-sm font-semibold px-3 py-2 rounded-lg">
                      <CalendarCheck className="w-4 h-4" /> 📋 Appel du jour (présences en 1 clic)
                    </button>
                  )}
                  {(s.enrollments || []).length === 0 && <p className="text-zinc-500 text-sm italic">Aucun duo inscrit.</p>}
                  {(s.enrollments || []).map((e) => (
                    <div key={e.eid} className="rounded-lg bg-white/5 p-3" data-testid={`samourais-duo-${e.eid}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm flex items-center gap-2">
                          {e.child_name} {e.child_age ? `(${e.child_age} ans)` : ""} <span className="text-zinc-500">· {e.parent_name}</span>
                          {(() => { const st = FOLLOWUP_STATUS[e.followup_status]; return st ? <span className={`px-1.5 py-0.5 rounded text-[10px] ${st.cls}`} data-testid={`duo-status-badge-${e.eid}`}>{st.label}</span> : null; })()}
                          {(() => { const att = e.attendance || {}; const tot = Object.keys(att).length; if (!tot) return null; const pct = Math.round(Object.values(att).filter(Boolean).length * 100 / tot); return <span className={`px-1.5 py-0.5 rounded text-[10px] ${pct >= 80 ? "bg-emerald-500/20 text-emerald-300" : pct >= 50 ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300"}`} title="Assiduité">📅 {pct}%</span>; })()}
                        </div>
                        <div className="text-xs text-zinc-400">{e.parent_email} {e.parent_phone ? `· ${e.parent_phone}` : ""} · {e.formule} · {e.amount} $
                          <span className={`ml-2 px-1.5 py-0.5 rounded ${e.payment_status === "paye" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{e.payment_status === "paye" ? (e.payment_source === "manual" ? "payé (manuel)" : "payé ✓ Stripe") : e.payment_state === "echec" ? "⛔ échec" : e.payment_state === "manuel_a_verifier" || e.payment_state === "virement_a_verifier" ? "à vérifier" : "rapprochement requis"}</span>
                          {(e.certificates || []).length > 0 && <span className="ml-2 text-yellow-300">🏅 {e.certificates.length}</span>}
                          {e.contract_id
                            ? <a href="/app/eleves" className="ml-2 px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 hover:underline" data-testid={`ps-contract-badge-${e.eid}`} title="Contrat élève relié">📄 contrat{e.consent?.signature_name ? " signé ✓" : ""}</a>
                            : <span className="ml-2 px-1.5 py-0.5 rounded bg-red-500/20 text-red-300" data-testid={`ps-contract-missing-${e.eid}`}>📄 sans contrat</span>}
                          {(e.followup_notes || []).length > 0 && <span className="ml-2 text-zinc-400">📝 {e.followup_notes.length}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setFollowupOpen((p) => ({ ...p, [e.eid]: !p[e.eid] }))} data-testid={`samourais-followup-toggle-${e.eid}`} title="Suivi professionnel" className="p-1.5 rounded-md bg-sky-600/80 hover:bg-sky-600"><ClipboardList className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setBilanDuo({ sid: s.id, enr: e })} data-testid={`samourais-bilan-${e.eid}`} title="Bilan de fin de bloc (PDF)" className="p-1.5 rounded-md bg-amber-600/80 hover:bg-amber-600"><FileText className="w-3.5 h-3.5" /></button>
                        {e.payment_status !== "paye" && (<>
                          <button onClick={() => resyncStripe(s.id, e.eid)} data-testid={`samourais-resync-${e.eid}`} title="Resynchroniser Stripe (vérifier si payé)" className="p-1.5 rounded-md bg-sky-700/80 hover:bg-sky-600 text-[11px] font-bold">↻$</button>
                          <button onClick={() => manualPayment(s.id, e)} data-testid={`samourais-manual-${e.eid}`} title="Confirmer un paiement manuel / virement" className="p-1.5 rounded-md bg-emerald-700/80 hover:bg-emerald-600 text-[11px] font-bold">💵</button>
                          <button onClick={() => sendLink(s.id, e.eid)} data-testid={`samourais-sendlink-${e.eid}`} title="Envoyer lien de paiement" className="p-1.5 rounded-md bg-violet-600/80 hover:bg-violet-600"><Send className="w-3.5 h-3.5" /></button>
                        </>)}
                        <button onClick={() => parentLink(s.id, e)} data-testid={`samourais-parent-link-${e.eid}`} title={e.parent_token ? "Espace parent (lien déjà créé) — copier / renvoyer" : "Créer l'espace parent"} className={`p-1.5 rounded-md text-[11px] ${e.parent_token ? "bg-teal-700/80 hover:bg-teal-600" : "bg-white/10 hover:bg-teal-600/60"}`}>👨‍👩‍👧</button>
                        <button onClick={() => setCarnetDuo({ sid: s.id, enr: e })} data-testid={`samourais-carnet-${e.eid}`} title="Carnet de progression (badges + certificats)" className="p-1.5 rounded-md bg-orange-600/80 hover:bg-orange-600 text-[11px] font-bold">📘</button>
                        <button onClick={() => addCert(s.id, e.eid)} title="Ajouter un certificat" className="p-1.5 rounded-md bg-white/10 hover:bg-white/20"><Award className="w-3.5 h-3.5" /></button>
                        <button onClick={() => delDuo(s.id, e.eid)} title="Retirer" className="p-1.5 rounded-md bg-white/10 hover:bg-red-500/40"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      </div>
                      {followupOpen[e.eid] && <DuoFollowup sid={s.id} enr={e} reload={load} />}
                    </div>
                  ))}
                  {(s.waitlist || []).length > 0 && (
                    <div className="pt-2">
                      <div className="text-xs text-amber-300 font-medium mb-1">Liste d'attente ({s.waitlist.length})</div>
                      {s.waitlist.map((w) => (
                        <div key={w.wid} className="text-xs text-zinc-400">• {w.child_name} · {w.parent_name} · {w.parent_phone || w.parent_email}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showBroadcast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="samourais-broadcast-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-bold">Message à tous les parents Samouraïs</h3>
            </div>
            <p className="text-xs text-zinc-400">Envoie un message à tous les parents inscrits aux sessions actives (un parent = un envoi, sans doublon).</p>
            <textarea data-testid="samourais-broadcast-text" value={bcMsg} onChange={(e) => setBcMsg(e.target.value)} rows={5}
              className="w-full rounded-xl bg-zinc-950 border border-white/10 p-3 text-sm text-zinc-100" />
            <div className="flex gap-5 text-sm">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={bcSms} onChange={(e) => setBcSms(e.target.checked)} className="accent-amber-500 w-4 h-4" /> SMS</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={bcEmail} onChange={(e) => setBcEmail(e.target.checked)} className="accent-amber-500 w-4 h-4" /> Courriel</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowBroadcast(false)} className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm">Annuler</button>
              <button data-testid="samourais-broadcast-send" onClick={sendBroadcast} disabled={bcSending}
                className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold text-sm disabled:opacity-50">
                {bcSending ? "Envoi…" : "Envoyer maintenant"}
              </button>
            </div>
            {bcHistory.length > 0 && (
              <div className="pt-3 mt-1 border-t border-white/10" data-testid="samourais-broadcast-history">
                <p className="text-xs font-semibold text-zinc-400 mb-2">Historique ({bcHistory.length})</p>
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {bcHistory.map((h) => (
                    <div key={h.id} className="text-xs bg-zinc-950 rounded-lg p-2 border border-white/5">
                      <div className="flex justify-between text-zinc-500">
                        <span>{new Date(h.created_at).toLocaleString("fr-CA")}</span>
                        <span>{h.recipient_count} parent(s) · {h.sms_sent} SMS · {h.email_sent} courriels{(h.sms_failed + h.email_failed) > 0 ? ` · ${h.sms_failed + h.email_failed} échec(s)` : ""}</span>
                      </div>
                      <p className="text-zinc-300 mt-1 line-clamp-2">{h.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {appelSession && <AppelDuJourModal session={appelSession} onClose={() => setAppelSession(null)} reload={() => { load(); loadAlerts(); }} />}
      {bilanDuo && <BilanModal sid={bilanDuo.sid} enr={bilanDuo.enr} onClose={() => setBilanDuo(null)} reload={load} />}
    </div>
  );
}

// 🧭 Suivi professionnel par famille : présence, statut, notes privées, rappel individuel
function DuoFollowup({ sid, enr, reload }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [rmMsg, setRmMsg] = useState(`Bonjour ${enr.parent_name || ""} ! Petit rappel : le prochain cours des Petits Samouraïs de ${enr.child_name} a lieu bientôt. On a hâte de vous voir sur le tatami ! 🥋`);
  const [rmChan, setRmChan] = useState("both");
  const att = enr.attendance || {};
  const notes = enr.followup_notes || [];

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible."); }
    finally { setBusy(false); }
  };

  const mark = (present) => run(
    () => api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/attendance`, { date, present }),
    present ? "Présence enregistrée." : "Absence enregistrée.");
  const setStatus = (status) => run(
    () => api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/status`, { status }), "Statut mis à jour.");
  const addNote = () => { if (!note.trim()) return; run(async () => { await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/note`, { text: note }); setNote(""); }, "Note ajoutée."); };
  const sendReminder = () => run(async () => {
    const r = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/reminder`, { message: rmMsg, channel: rmChan });
    setShowReminder(false);
    toast.success(`Rappel envoyé${r.data.email_sent ? " (courriel)" : ""}${r.data.sms_sent ? " + SMS" : ""}${!r.data.email_sent && !r.data.sms_sent ? " — aucun canal disponible" : ""}`);
  });

  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-4" data-testid={`duo-followup-${enr.eid}`}>
      {/* Statut de suivi */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Statut de suivi</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(FOLLOWUP_STATUS).map(([k, v]) => (
            <button key={k} disabled={busy} onClick={() => setStatus(k)} data-testid={`duo-status-${enr.eid}-${k}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition disabled:opacity-50 ${enr.followup_status === k ? v.cls + " border-transparent" : "border-white/15 text-zinc-400 hover:text-zinc-200"}`}>{v.label}</button>
          ))}
        </div>
      </div>

      {/* Présence par séance */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> Présence par séance</div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} data-testid={`duo-att-date-${enr.eid}`}
            className="bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-200" />
          <button disabled={busy} onClick={() => mark(true)} data-testid={`duo-att-present-${enr.eid}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${att[date] === true ? "bg-emerald-600 text-white" : "bg-white/5 hover:bg-white/10 text-zinc-300"}`}>✓ Présent</button>
          <button disabled={busy} onClick={() => mark(false)} data-testid={`duo-att-absent-${enr.eid}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${att[date] === false ? "bg-red-600 text-white" : "bg-white/5 hover:bg-white/10 text-zinc-300"}`}>✗ Absent</button>
          {Object.keys(att).length > 0 && (
            <span className="text-[11px] text-zinc-400">Assiduité : <b className="text-zinc-200">{Math.round(Object.values(att).filter(Boolean).length * 100 / Object.keys(att).length)}%</b> ({Object.values(att).filter(Boolean).length}/{Object.keys(att).length})</span>
          )}
        </div>
        {Object.keys(att).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(att).sort().map(([d, p]) => (
              <span key={d} className={`px-1.5 py-0.5 rounded text-[10px] ${p ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{d.slice(5)} {p ? "✓" : "✗"}</span>
            ))}
          </div>
        )}
      </div>

      {/* Rappel individuel */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Bell className="w-3 h-3" /> Rappel individuel</div>
          <button disabled={busy} onClick={() => setShowReminder((v) => !v)} data-testid={`duo-reminder-toggle-${enr.eid}`}
            className="text-[11px] px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold disabled:opacity-50">{showReminder ? "Fermer" : "✉️ Envoyer un rappel"}</button>
        </div>
        {showReminder && (
          <div className="space-y-2">
            <textarea rows={3} value={rmMsg} onChange={(ev) => setRmMsg(ev.target.value)} data-testid={`duo-reminder-msg-${enr.eid}`}
              className="w-full bg-zinc-950 border border-white/10 rounded-lg p-2 text-xs text-zinc-100" />
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-zinc-900 border border-white/10 rounded-lg p-0.5">
                {[["both", "✉️+💬"], ["email", "✉️ Courriel"], ["sms", "💬 SMS"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRmChan(v)} data-testid={`duo-reminder-chan-${enr.eid}-${v}`}
                    className={`px-2 py-1 rounded text-[11px] ${rmChan === v ? "bg-amber-500 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
                ))}
              </div>
              <button disabled={busy} onClick={sendReminder} data-testid={`duo-reminder-send-${enr.eid}`}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50">Envoyer</button>
            </div>
          </div>
        )}
      </div>

      {/* Notes de suivi privées */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1"><NotebookPen className="w-3 h-3" /> Notes de suivi privées</div>
        {notes.length > 0 && (
          <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto" data-testid={`duo-notes-${enr.eid}`}>
            {notes.slice().reverse().map((n, i) => (
              <div key={i} className="text-xs bg-zinc-950 rounded-lg p-2 border border-white/5">
                <div className="text-[10px] text-zinc-500">{new Date(n.at).toLocaleString("fr-CA")} · {n.by}</div>
                <div className="text-zinc-300">{n.text}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={note} onChange={(ev) => setNote(ev.target.value)} data-testid={`duo-note-input-${enr.eid}`}
            onKeyDown={(ev) => { if (ev.key === "Enter") addNote(); }}
            placeholder="Observation du coach, progrès de l'enfant…"
            className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-200" />
          <button disabled={busy || !note.trim()} onClick={addNote} data-testid={`duo-note-add-${enr.eid}`}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium disabled:opacity-50">Ajouter</button>
        </div>
      </div>
    </div>
  );
}


// 📋 Appel du jour — coche présents/absents de tous les duos d'un coup
function AppelDuJourModal({ session, onClose, reload }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const enrs = session.enrollments || [];
  const [marks, setMarks] = useState(() => {
    const m = {}; enrs.forEach((e) => { m[e.eid] = true; }); return m;
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const records = enrs.map((e) => ({ eid: e.eid, present: !!marks[e.eid] }));
      const { data } = await api.post(`/samourais/sessions/${session.id}/attendance-bulk`, { date, records });
      if ((data.alerts || []).length > 0) {
        toast.warning(`⚠️ ${data.alerts.length} enfant(s) avec 2 absences de suite — à relancer`);
      }
      toast.success(`Appel enregistré (${data.marked} duos, ${date}).`);
      reload(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur enregistrement appel."); }
    setBusy(false);
  };

  const presentCount = Object.values(marks).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="samourais-appel-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-indigo-400" /> Appel du jour</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-zinc-400">{session.title} · {session.days} · {session.time}</div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-400">Date du cours :</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appel-date"
            className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-zinc-100" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMarks(Object.fromEntries(enrs.map((e) => [e.eid, true])))} data-testid="appel-all-present"
            className="flex-1 text-xs py-1.5 rounded-lg bg-emerald-700/40 hover:bg-emerald-700/60 text-emerald-200">Tous présents</button>
          <button onClick={() => setMarks(Object.fromEntries(enrs.map((e) => [e.eid, false])))} data-testid="appel-all-absent"
            className="flex-1 text-xs py-1.5 rounded-lg bg-red-700/40 hover:bg-red-700/60 text-red-200">Tous absents</button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="appel-list">
          {enrs.map((e) => (
            <button key={e.eid} onClick={() => setMarks((m) => ({ ...m, [e.eid]: !m[e.eid] }))} data-testid={`appel-toggle-${e.eid}`}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition ${marks[e.eid] ? "border-emerald-600/50 bg-emerald-900/20 text-emerald-100" : "border-red-600/50 bg-red-900/20 text-red-100"}`}>
              <span>{e.child_name} <span className="text-zinc-500 text-xs">· {e.parent_name}</span></span>
              <span className="text-xs font-semibold">{marks[e.eid] ? "✓ Présent" : "✗ Absent"}</span>
            </button>
          ))}
        </div>
        <div className="text-xs text-zinc-400">{presentCount}/{enrs.length} présents</div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm">Annuler</button>
          <button onClick={save} disabled={busy} data-testid="appel-save"
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm disabled:opacity-50">
            {busy ? "Enregistrement…" : "Enregistrer l'appel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 📄 Bilan de fin de bloc (imprimable / PDF + envoi au parent)
function BilanModal({ sid, enr, onClose, reload }) {
  const [bilan, setBilan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/samourais/sessions/${sid}/enrollment/${enr.eid}/bilan`); setBilan(data); }
      catch { toast.error("Erreur chargement du bilan."); }
    })();
  }, [sid, enr.eid]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/bilan/send`);
      toast.success(data.sent ? `Bilan envoyé à ${data.email}` : "Envoi courriel indisponible (vérifie Resend/Gmail).");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi."); }
    setBusy(false);
  };

  const printBilan = () => {
    const w = window.open("", "_blank");
    if (!w || !bilan) return;
    const coachNotes = (bilan.notes || []).filter((n) => n.by !== "système");
    const dates = Object.entries(bilan.attendance || {}).sort();
    w.document.write(`<html><head><title>Bilan ${bilan.child_name}</title>
      <style>body{font-family:Arial;max-width:720px;margin:24px auto;color:#111;padding:0 16px}
      h1{color:#b91c1c;margin-bottom:4px} .muted{color:#666;font-size:13px}
      .box{border:2px solid #c9a961;border-radius:12px;padding:16px;margin:16px 0}
      .pct{font-size:34px;font-weight:800;color:#059669} ul{margin:6px 0} li{margin:3px 0}
      .day{display:inline-block;margin:2px;padding:2px 8px;border-radius:10px;font-size:12px}
      .p{background:#d1fae5;color:#065f46}.a{background:#fee2e2;color:#991b1b}</style></head><body>
      <h1>Bilan des Petits Samouraïs 🥋</h1>
      <div class="muted">${bilan.session?.title || ""} · ${bilan.session?.days || ""} ${bilan.session?.time || ""}</div>
      <div class="box"><b>Enfant :</b> ${bilan.child_name}${bilan.child_age ? ` (${bilan.child_age} ans)` : ""}<br/>
      <b>Parent :</b> ${bilan.parent_name || "—"}</div>
      <div class="box"><div class="muted">Assiduité sur le bloc</div>
      <div class="pct">${bilan.pct}%</div><div>${bilan.present}/${bilan.total} présences</div>
      <div style="margin-top:8px">${dates.map(([d, p]) => `<span class="day ${p ? "p" : "a"}">${d.slice(5)} ${p ? "✓" : "✗"}</span>`).join("")}</div></div>
      ${coachNotes.length ? `<div class="box"><div class="muted">Observations du coach</div><ul>${coachNotes.map((n) => `<li>${n.text}</li>`).join("")}</ul></div>` : ""}
      <p style="color:#666">Merci pour votre confiance — au plaisir de continuer l'aventure !<br/>— Institut Impact Élite Martial · (819) 527-9446</p>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="samourais-bilan-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-amber-400" /> Bilan de fin de bloc</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-5 h-5" /></button>
        </div>
        {!bilan ? (
          <p className="text-zinc-500 text-sm py-6 text-center">Chargement…</p>
        ) : (
          <>
            <div className="rounded-xl bg-white/5 p-4">
              <div className="font-semibold">{bilan.child_name} {bilan.child_age ? `(${bilan.child_age} ans)` : ""}</div>
              <div className="text-xs text-zinc-400">{bilan.parent_name} · {bilan.parent_email || "aucun courriel"}</div>
              <div className="mt-3 flex items-center gap-3">
                <div className="text-3xl font-extrabold text-emerald-400">{bilan.pct}%</div>
                <div className="text-sm text-zinc-300">{bilan.present}/{bilan.total} présences</div>
              </div>
              {(bilan.notes || []).filter((n) => n.by !== "système").length > 0 && (
                <div className="mt-3 text-xs text-zinc-400">📝 {(bilan.notes || []).filter((n) => n.by !== "système").length} observation(s) du coach incluse(s)</div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={printBilan} data-testid="bilan-print"
                className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-xl text-sm">
                <FileText className="w-4 h-4" /> Imprimer / PDF
              </button>
              <button onClick={send} disabled={busy || !bilan.parent_email} data-testid="bilan-send"
                className="flex-1 flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white font-semibold px-4 py-2 rounded-xl text-sm">
                <Send className="w-4 h-4" /> {busy ? "Envoi…" : "Envoyer au parent"}
              </button>
            </div>
            {!bilan.parent_email && <p className="text-[11px] text-zinc-600 text-center">Aucun courriel parent : utilise « Imprimer / PDF ».</p>}
          </>
        )}
      </div>
    </div>
  );
}
